import {
  ETACheckbox,
  ETAFooterContainer,
  ETAInput,
  ETANotificationCreatePage,
  ETANotificationFormContainer,
  ETASwitchButton,
  FormColumnBlock,
  FormRowBlock,
  HeaderText,
  SubHeaderText
} from "../common/ETANotificationConfigs.style";
import {
  useETANotificationContext,
  ETANotificationConfigsFormValues,
  SendNotificationType
} from "../ETANotificationConfigsProvider";
import {Controller, ControllerFieldState, ControllerRenderProps, useWatch} from "react-hook-form";
import {
  SearchableSortDirection,
  ServiceFilter,
  ServiceSortableField,
  useSearchServicesQuery,
  CustomerFilter,
  CustomerSortableField,
  useSearchCustomersQuery,
  useGetDistinctValuesQuery,
  SearchableVehicleTypeSortableFields,
  ModelVehicleTypeFilterInput,
  useSearchVehicleTypesQuery,
  SearchableOrderPrioritySortableFields,
  ModelOrderPriorityFilterInput,
  useSearchOrderPrioritiesQuery,
  SiteSortableField,
  SiteFilterInput,
  useSearchSitesQuery,
  DriverFilterInput,
  SearchableDriverSortableFields,
  useSearchDriversEnhancedQuery,
  ModelDispatchGroupFilterInput,
  SearchableDispatchGroupSortableFields,
  useSearchDispatchGroupsQuery
} from "../../../../../generated/graphql";
import {ChangeEvent, EventHandler, useCallback, useContext, useEffect, useRef, useState, useMemo} from "react";
import {OptionType} from "../../../../common/types";
import {AuthContext} from "../../../../../providers/AuthProvider";

import {andSearchWithWildcards} from "../../../ColorSettings/ColorizedIndicators/RuleGenerator.service";

import {getArrayFromField, getOptionsFromArrayOfObjects} from "../../../../../utils/General";
import {Constants} from "../../../../../constants/constants";
import {
  buildFollowUpPartialMessage,
  getFollowUpEligibility,
  validateSecondNotificationOrdering,
  STOP_TYPE_DISPLAY_LABELS,
  formatStopTypesForDisplay
} from "../common/ETANotificationConfigs.utils";
import {ETASelectionRenderer} from "./ETASelectionRenderer";
import {ETAAttributeSelect} from "./ETAAttributeSelect";
import {ETANotificationSettings} from "./ETANotificationSettings";
import {ETATriggerSettings} from "./ETATriggerSettings";
import {ETANotificationTimeline} from "./ETANotificationTimeline";
import {ETAInputWrapperWithErrorMessage} from "./ETAInputWrapperWithErrorMessage";
import {NoBackGroundSettingEditorButton, SettingEditorButton} from "../../../components/SettingItemEditor";
import {ConfirmationDialog} from "../../../../common/ConfirmationDialog";
import {PremiumUpgradePopover} from "../common/PremiumUpgradePopover";
import {useFeatureFlagContext} from "../../../../../providers/FeatureFlagProvider";
import {Tooltip} from "@blueprintjs/core";
import {InformationIcon} from "../../../../common/icons";
import {BOTH_STOP_TOOLTIP} from "../../../../common/bothStopTooltip";

const STOP_TYPES_OPTIONS: OptionType[] = [
  {key: "P", value: "Pickup"},
  {key: "D", value: "Delivery"},
  {key: "B", value: "Both"}
];

export const ETANotificationForm = () => {
  const {etaNotificationPremium} = useFeatureFlagContext();
  const {isEcourierUser, isSuperUser} = useContext(AuthContext);

  // Internal e-Courier admins bypass the premium UI lock; external/customer admins remain gated.
  const isInternalSuperAdmin = useMemo(() => {
    return Boolean(isEcourierUser && isSuperUser);
  }, [isEcourierUser, isSuperUser]);
  const canEditPremium = etaNotificationPremium || isInternalSuperAdmin;

  const {
    includeOptimizationETAEnabled,
    etaFormState,
    formMode,
    onCreateNew,
    onCancel,
    onSave,
    onDelete,
    isFormValid,
    isDirty
  } = useETANotificationContext();
  const {control, setValue, clearErrors, getValues, trigger} = etaFormState;

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [services, setServices] = useState<OptionType[]>([]);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceFilter | undefined>(undefined);
  const [serviceTypeQuery, setServiceTypeQuery] = useState<string>("");

  const [stopTypeQuery, setStopTypeQuery] = useState<string>("");

  const [customers, setCustomers] = useState<OptionType[]>([]);
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter | undefined>(undefined);
  const [customerQuery, setCustomerQuery] = useState<string>("");

  const [customerGroups, setCustomerGroups] = useState<OptionType[]>([]);
  const [customerGroupQuery, setCustomerGroupQuery] = useState<string>("");

  const [vehicleTypes, setVehicleTypes] = useState<OptionType[]>([]);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<ModelVehicleTypeFilterInput | undefined>(undefined);
  const [vehicleTypeQuery, setVehicleTypeQuery] = useState<string>("");

  const [priorities, setPriorities] = useState<OptionType[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<ModelOrderPriorityFilterInput | undefined>(undefined);
  const [priorityQuery, setPriorityQuery] = useState<string>("");

  const [sites, setSites] = useState<OptionType[]>([]);
  const [siteFilter, setSiteFilter] = useState<SiteFilterInput | undefined>(undefined);
  const [siteQuery, setSiteQuery] = useState<string>("");

  const [drivers, setDrivers] = useState<OptionType[]>([]);
  const [driverFilter, setDriverFilter] = useState<DriverFilterInput | undefined>(undefined);
  const [driverQuery, setDriverQuery] = useState<string>("");

  const [driverGroups, setDriverGroups] = useState<OptionType[]>([]);
  const [driverGroupFilter, setDriverGroupFilter] = useState<ModelDispatchGroupFilterInput | undefined>({
    dispatchType: {eq: "D"}
  });
  const [driverGroupQuery, setDriverGroupQuery] = useState<string>("");

  useEffect(() => {
    if (serviceTypeQuery?.trim()) {
      const tokens = serviceTypeQuery.trim().split(" ");
      setServiceTypeFilter(andSearchWithWildcards("description", tokens));
    } else {
      setServiceTypeFilter(undefined);
    }
  }, [serviceTypeQuery]);

  useEffect(() => {
    if (customerQuery?.trim()) {
      const tokens = customerQuery.trim().split(" ");
      setCustomerFilter(andSearchWithWildcards("name", tokens));
    } else {
      setCustomerFilter(undefined);
    }
  }, [customerQuery]);

  useEffect(() => {
    if (vehicleTypeQuery?.trim()) {
      const tokens = vehicleTypeQuery.trim().split(" ");
      setVehicleTypeFilter(andSearchWithWildcards("description", tokens));
    } else {
      setVehicleTypeFilter(undefined);
    }
  }, [vehicleTypeQuery]);

  useEffect(() => {
    if (priorityQuery?.trim()) {
      const tokens = priorityQuery.trim().split(" ");
      setPriorityFilter(andSearchWithWildcards("description", tokens));
    } else {
      setPriorityFilter(undefined);
    }
  }, [priorityQuery]);

  useEffect(() => {
    if (siteQuery?.trim()) {
      const tokens = siteQuery.trim().split(" ");
      setSiteFilter(andSearchWithWildcards("code", tokens));
    } else {
      setSiteFilter(undefined);
    }
  }, [siteQuery]);

  useEffect(() => {
    setDriverFilter({
      driver: {
        driverStatus: {eq: "A"}
      },
      ...(driverQuery.length > 0 && {
        multiFieldWildcard: {
          wildcard: `*${driverQuery}*`,
          caseInsensitive: true,
          fields: ["name.keyword", "driverCode.keyword"]
        }
      })
    });
  }, [driverQuery]);

  useEffect(() => {
    if (driverGroupQuery?.trim()) {
      const tokens = driverGroupQuery.trim().split(" ");
      setDriverGroupFilter({
        ...andSearchWithWildcards("description", tokens),
        dispatchType: {eq: "D"}
      });
    } else {
      setDriverGroupFilter({
        dispatchType: {eq: "D"}
      });
    }
  }, [driverGroupQuery]);

  const filteredStopTypes = useMemo(() => {
    return STOP_TYPES_OPTIONS.filter((option) => {
      if (!stopTypeQuery.trim()) return true;
      const searchLower = stopTypeQuery.toLowerCase();
      return (
        String(option.key).toLowerCase().includes(searchLower) ||
        String(option.value).toLowerCase().includes(searchLower)
      );
    });
  }, [stopTypeQuery]);

  const servicesResults = useSearchServicesQuery({
    variables: {
      limit: 200,
      sort: {
        field: ServiceSortableField.Description,
        direction: SearchableSortDirection.Asc
      },
      filter: {
        ...serviceTypeFilter,
        status: {
          match: "A"
        }
      }
    }
  });

  const customersResults = useSearchCustomersQuery({
    variables: {
      limit: 200,
      sort: {
        field: CustomerSortableField.Name,
        direction: SearchableSortDirection.Asc
      },
      filter: customerFilter
    }
  });

  const customerGroupsResults = useGetDistinctValuesQuery({
    variables: {
      field: "customerGroup.keyword",
      typeName: "ng_customer",
      limit: Constants.DISPATCH_DATA_LIMIT
    }
  });

  const vehicleTypesResults = useSearchVehicleTypesQuery({
    variables: {
      limit: 200,
      sort: {
        field: SearchableVehicleTypeSortableFields.Description,
        direction: SearchableSortDirection.Asc
      },
      filter: vehicleTypeFilter
    }
  });

  const prioritiesResults = useSearchOrderPrioritiesQuery({
    variables: {
      ...{
        limit: 200,
        sort: {
          field: SearchableOrderPrioritySortableFields.Description,
          direction: SearchableSortDirection.Asc
        },
        filter: priorityFilter
      }
    }
  });

  const sitesResults = useSearchSitesQuery({
    variables: {
      limit: 200,
      sort: {
        field: SiteSortableField.Code,
        direction: SearchableSortDirection.Asc
      },
      filter: {
        ...siteFilter,
        status: {
          eq: "A"
        }
      }
    }
  });

  const driversResults = useSearchDriversEnhancedQuery({
    variables: {
      limit: 100,
      sort: [
        {
          field: SearchableDriverSortableFields.Name,
          direction: SearchableSortDirection.Asc
        }
      ],
      filter: driverFilter,
      offset: 0
    }
  });

  const driverGroupsResults = useSearchDispatchGroupsQuery({
    variables: {
      limit: 200,
      sort: [
        {
          field: SearchableDispatchGroupSortableFields.Description,
          direction: SearchableSortDirection.Asc
        }
      ],
      filter: driverGroupFilter
    }
  });

  useEffect(() => {
    if (servicesResults.data?.searchServices?.items) {
      const serviceOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(servicesResults.data.searchServices.items),
        "service",
        "description"
      );
      setServices(serviceOptions);
    }
  }, [servicesResults.data]);

  useEffect(() => {
    if (customersResults.data?.searchCustomers?.items) {
      const customerOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(customersResults.data.searchCustomers.items),
        "customerId",
        "name"
      );
      setCustomers(customerOptions);
    }
  }, [customersResults.data]);

  useEffect(() => {
    if (customerGroupsResults.data?.getDistinctValues?.items) {
      const groupOptions: OptionType[] = customerGroupsResults.data.getDistinctValues.items
        .filter((item) => item?.key)
        .map((item) => ({key: item!.key!, value: item!.key!}))
        .sort((a, b) => String(a.value).localeCompare(String(b.value)));
      setCustomerGroups(groupOptions);
    }
  }, [customerGroupsResults.data]);

  const filteredCustomerGroups = useMemo(() => {
    if (!customerGroupQuery.trim()) return customerGroups;
    const searchLower = customerGroupQuery.toLowerCase();
    return customerGroups.filter(
      (option) =>
        String(option.key).toLowerCase().includes(searchLower) ||
        String(option.value).toLowerCase().includes(searchLower)
    );
  }, [customerGroupQuery, customerGroups]);

  useEffect(() => {
    if (vehicleTypesResults.data?.searchVehicleTypes?.items) {
      const vehicleTypeOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(vehicleTypesResults.data.searchVehicleTypes.items),
        "vehicleTypeId",
        "description"
      );
      setVehicleTypes(vehicleTypeOptions);
    }
  }, [vehicleTypesResults.data]);

  useEffect(() => {
    if (prioritiesResults.data?.searchOrderPriorities?.items) {
      const priorityOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(prioritiesResults.data.searchOrderPriorities.items),
        "orderPriorityId",
        "description"
      );
      setPriorities(priorityOptions);
    }
  }, [prioritiesResults.data]);

  useEffect(() => {
    if (sitesResults.data?.searchSites?.items) {
      const siteOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(sitesResults.data.searchSites.items),
        "siteId",
        "code"
      );
      setSites(siteOptions);
    }
  }, [sitesResults.data]);

  useEffect(() => {
    if (driversResults.data?.searchDriversEnhanced?.items) {
      const driverOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(driversResults.data.searchDriversEnhanced.items),
        "driverId",
        "name"
      );
      setDrivers(driverOptions);
    }
  }, [driversResults.data]);

  useEffect(() => {
    if (driverGroupsResults.data?.searchDispatchGroups?.items) {
      const driverGroupOptions = getOptionsFromArrayOfObjects(
        getArrayFromField(driverGroupsResults.data.searchDispatchGroups.items),
        "dispatchGroupId",
        "dispatchGroup"
      );
      setDriverGroups(driverGroupOptions);
    }
  }, [driverGroupsResults.data]);

  const handleSaveClick = async () => {
    // DP-3044: re-run validation before saving so an invalid 1st/2nd pair cannot be
    // submitted even if the Save button is force-enabled (e.g. disabled attr removed).
    const isValid = await trigger();
    if (!isValid) return;
    void onSave();
  };

  const handleDeleteClick = () => {
    void onDelete();
  };

  const configId = useWatch({
    control,
    name: "id"
  });

  const attributesValues = useWatch({
    control,
    name: "attributes"
  });

  const isUseSecondNotification = useWatch({
    control,
    name: "secondNotification.enabled"
  });

  const optimizationNotificationEnabled = useWatch({
    control,
    name: "optimizationNotification.enabled"
  });

  const firstNotification = useWatch({
    control,
    name: "firstNotification"
  });

  const secondNotification = useWatch({
    control,
    name: "secondNotification"
  });

  // DP-3044: when both notifications share the same trigger type, the 2nd's value
  // must be strictly fewer than the 1st's. Pure rule lives in the utils helper so it
  // can be unit-tested in isolation; here we just feed it the current form values.
  const validateSecondNotificationAwayFrom = useCallback(
    (value: unknown): true | string =>
      validateSecondNotificationOrdering(getValues("firstNotification"), getValues("secondNotification"), value),
    [getValues]
  );

  // The 2nd's validity depends on the 1st's value/type and the 2nd's type, but
  // react-hook-form only re-validates a field when that field itself changes.
  // Re-trigger the 2nd's validation when any dependency changes so a stale error
  // appears/clears without the user touching the 2nd input.
  useEffect(() => {
    // Only re-run the cross-field check once the 2nd already has a value. Triggering on
    // an empty field would surface its "required" error before the user has touched it
    // (e.g. right after enabling the 2nd notification).
    if (isUseSecondNotification && getValues("secondNotification.awayFrom")) {
      void trigger("secondNotification.awayFrom");
    }
  }, [
    firstNotification?.awayFrom,
    firstNotification?.triggerWhen,
    secondNotification?.triggerWhen,
    isUseSecondNotification,
    getValues,
    trigger
  ]);

  const sendNotificationVia = useWatch({
    control,
    name: "sendNotificationVia"
  });

  const [followUpError, setFollowUpError] = useState<string | null>(null);
  const [followUpInfoMessage, setFollowUpInfoMessage] = useState<string | null>(null);
  const [validSnapshot, setValidSnapshot] = useState<{
    firstNotification: typeof firstNotification;
    secondNotification: typeof secondNotification;
  } | null>(null);
  const [showRevertDialog, setShowRevertDialog] = useState(false);

  // DP-3095 — Follow Up eligibility is per notification type (1st + enabled-2nd). Optimization
  // is intentionally excluded (ETA does not drift from optimization).
  const followUpEligibility = useMemo(
    () => getFollowUpEligibility(firstNotification, secondNotification),
    [firstNotification, secondNotification]
  );

  // A coarse signature of the qualifying state. The reactive popup / revert dialog must fire only
  // when this signature *changes* — editing an unrelated 2nd-notification field (e.g. awayFrom)
  // still re-runs the effect (validSnapshot is re-snapshotted every keystroke) but must NOT re-pop.
  const followUpStateSignature = !followUpEligibility.anyQualifies
    ? "none"
    : followUpEligibility.isPartial
    ? `partial:${followUpEligibility.first ? "first" : "second"}`
    : "all";
  const followUpStateRef = useRef<string | null>(null);

  useEffect(() => {
    if (followUpError && followUpEligibility.anyQualifies) {
      setFollowUpError(null);
    }
  }, [followUpError, followUpEligibility]);

  useEffect(() => {
    setFollowUpError(null);
    setFollowUpInfoMessage(null);
    setShowRevertDialog(false);
    // Seed the transition guard with the loaded state so opening an existing config doesn't
    // auto-pop the dialog/popup; only a later user edit that changes the state should fire it.
    const isFollowedUp = getValues("isFollowedUp");
    followUpStateRef.current = isFollowedUp ? followUpStateSignature : null;
    if (isFollowedUp && followUpEligibility.anyQualifies) {
      setValidSnapshot({
        firstNotification: {...getValues("firstNotification")},
        secondNotification: {...getValues("secondNotification")}
      });
    } else {
      setValidSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId, formMode]);

  useEffect(() => {
    const isFollowedUp = getValues("isFollowedUp");
    if (isFollowedUp && followUpEligibility.anyQualifies) {
      setValidSnapshot({
        firstNotification: {...firstNotification},
        secondNotification: {...secondNotification}
      });
    }
  }, [firstNotification, secondNotification, followUpEligibility, getValues]);

  useEffect(() => {
    const isFollowedUp = getValues("isFollowedUp");

    if (!isFollowedUp || !validSnapshot) {
      // Do NOT reset followUpStateRef here. On load, the [configId, formMode] effect seeds the ref
      // and calls setValidSnapshot — but validSnapshot only commits next render, so this branch runs
      // once with validSnapshot still null. Nulling the ref here wiped that seed, so when the
      // snapshot committed the signature looked like a fresh transition and the popup fired on every
      // open. The ref is already (re)seeded on load/cancel/create and on check/uncheck, so leave it.
      return;
    }

    // Fire only on an actual transition. Without this guard the popup re-appears on every
    // 2nd-notification edit, because validSnapshot is re-snapshotted each keystroke and re-runs
    // this effect while the state stays partial. Note: the timer is intentionally NOT cleared on
    // every re-run — only when a real transition schedules a new one — so a pending popup survives
    // same-state edits (unmount cleanup lives in its own effect below).
    if (followUpStateSignature === followUpStateRef.current) {
      return;
    }
    followUpStateRef.current = followUpStateSignature;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!followUpEligibility.anyQualifies) {
      // No type qualifies anymore → offer to revert windows or drop Follow Up (existing dialog).
      debounceTimerRef.current = setTimeout(() => {
        setShowRevertDialog(true);
      }, 500);
    } else if (followUpEligibility.isPartial) {
      // One type still qualifies → keep Follow Up on, just inform which type won't get follow-ups.
      debounceTimerRef.current = setTimeout(() => {
        setFollowUpInfoMessage(buildFollowUpPartialMessage(followUpEligibility));
      }, 500);
    }
  }, [followUpEligibility, followUpStateSignature, validSnapshot, getValues]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  const renderNameInput = ({
    field,
    fieldState
  }: {
    field: ControllerRenderProps<ETANotificationConfigsFormValues, "name">;
    fieldState: ControllerFieldState;
  }) => {
    return (
      <ETAInputWrapperWithErrorMessage errorMessage={fieldState.error?.message} width={"100%"}>
        <ETAInput {...field} data-testid="eta-name-input" />
      </ETAInputWrapperWithErrorMessage>
    );
  };

  const renderSecondNotificationSwitch = ({
    field
  }: {
    field: ControllerRenderProps<ETANotificationConfigsFormValues, "secondNotification.enabled">;
  }) => {
    const {value, onChange, disabled} = field;
    const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checked) {
        clearErrors("secondNotification");
      }
      onChange(event.target.checked);
    };
    return (
      <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
        <ETASwitchButton
          checked={value}
          onChange={handleSwitchChange}
          size={"large"}
          label={"2nd Notification (Optional)"}
          disabled={disabled}
        />
        {!canEditPremium && <PremiumUpgradePopover />}
      </div>
    );
  };

  const renderOptimizationNotificationSwitch = ({
    field
  }: {
    field: ControllerRenderProps<ETANotificationConfigsFormValues, "optimizationNotification.enabled">;
  }) => {
    const {value, onChange} = field;
    const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checked) {
        clearErrors("optimizationNotification");
      }
      onChange(event.target.checked);
    };
    return (
      <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
        <ETASwitchButton
          checked={value}
          onChange={handleSwitchChange}
          size={"large"}
          label={"Optimization Notification"}
        />
        <Tooltip
          content={
            <div>
              <p>
                Turn this on if you&apos;d like ETA time windows sent to your customers at the time of optimization.
                Theoretically, these are lower confidence ETAs than ones generated live by the driver actions.
              </p>
              <p>
                In the event that multiple optimizations happen on a manifest we&apos;ll send notifications each time
                the a manifest with no completed stops is optimized based on your defined logic. If a manifest has stops
                completed and is in progress we will only send notifications based on the other logics setup below in
                &apos;1st Notification&apos; and others.
              </p>
            </div>
          }
          placement="top"
          popoverClassName="content-tooltip"
        >
          <div data-testid="information-icon" style={{display: "flex", alignItems: "center"}}>
            <InformationIcon scale={0.8} />
          </div>
        </Tooltip>
      </div>
    );
  };

  const handleCancelInvalidChange = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (validSnapshot) {
      setValue("firstNotification", validSnapshot.firstNotification);
      setValue("secondNotification", validSnapshot.secondNotification);
      clearErrors("firstNotification");
      clearErrors("secondNotification");
    }

    setTimeout(() => {
      setFollowUpError(null);
    }, 0);

    setShowRevertDialog(false);
  };

  const handleConfirmInvalidChange = () => {
    setValue("isFollowedUp", false);
    setValidSnapshot(null);
    setShowRevertDialog(false);
  };

  const renderIsFollowedUpCheckbox = ({
    field
  }: {
    field: ControllerRenderProps<ETANotificationConfigsFormValues, "isFollowedUp">;
  }) => {
    const {value, onChange, disabled} = field;
    const handleCheckboxChange: EventHandler<ChangeEvent<HTMLInputElement>> = (e) => {
      const isChecking = e.target.checked;

      if (isChecking) {
        if (!followUpEligibility.anyQualifies) {
          // No type qualifies → block the check with today's inline error.
          setFollowUpError(
            "Arguments must include time windows that are at least 15 minutes before and 30 minutes after in order to send follow up ETAs."
          );
          return;
        }

        setValidSnapshot({
          firstNotification: {...firstNotification},
          secondNotification: {...secondNotification}
        });

        if (followUpEligibility.isPartial) {
          // One type qualifies → allow the check, inform which type won't get follow-ups.
          setFollowUpInfoMessage(buildFollowUpPartialMessage(followUpEligibility));
        }
        // Seed the guard to the just-checked state so the reactive effect doesn't re-schedule
        // the popup we (may have) shown synchronously above.
        followUpStateRef.current = followUpStateSignature;
      } else {
        setValidSnapshot(null);
        followUpStateRef.current = null;
      }

      setFollowUpError(null);
      onChange(e.target.checked);
    };

    return (
      <ETAInputWrapperWithErrorMessage errorMessage={followUpError || undefined}>
        <ETACheckbox checked={value} onChange={handleCheckboxChange} disabled={disabled}>
          Send an updated ETA time range if the ETA falls outside of the initial ETA time window sent to the customer
        </ETACheckbox>
      </ETAInputWrapperWithErrorMessage>
    );
  };

  return (
    <ETANotificationFormContainer data-testid="eta-notification-form-container">
      <FormColumnBlock padding={"0"}>
        <HeaderText fontSize={"24px"}>New logic</HeaderText>
        <SubHeaderText fontSize={"16px"}>Here you can setup the ETA Notification settings</SubHeaderText>
      </FormColumnBlock>
      {!formMode ? (
        <ETANotificationCreatePage data-testid="eta-create-new-page">
          <ETACreateNewIcon />
          <SubHeaderText>Create a new logic or edit an existing one to see its details here</SubHeaderText>
          <SettingEditorButton onClick={onCreateNew} data-testid="create-logic-button">
            Create Logic
          </SettingEditorButton>
        </ETANotificationCreatePage>
      ) : (
        <>
          <FormColumnBlock withDivider={true}>
            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
              <HeaderText>Name</HeaderText>
              <Tooltip
                content={
                  <div>
                    <p>
                      Name this something that will make it easy for you to remember what it represents in the future.
                    </p>
                  </div>
                }
                placement="top"
                popoverClassName="content-tooltip"
              >
                <div data-testid="name-information-icon" style={{display: "flex", alignItems: "center"}}>
                  <InformationIcon scale={0.8} />
                </div>
              </Tooltip>
            </div>
            <Controller
              render={renderNameInput}
              control={control}
              name="name"
              rules={{
                required: {value: true, message: "Name is required"}
              }}
            />
          </FormColumnBlock>
          <FormColumnBlock withDivider={true} gap="8px">
            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
              <HeaderText>Who Should This Logic Apply To?</HeaderText>
              <Tooltip
                content={
                  <div>
                    <p>
                      By using these filters, you can choose to only send ETA time windows for certain attributes such
                      as customer name, or service types, or a combo of multiple. Or, you can send wider or more narrow
                      time window expectation depending on the work type.
                    </p>
                  </div>
                }
                placement="top"
                popoverClassName="content-tooltip"
              >
                <div data-testid="who-is-this-information-icon" style={{display: "flex", alignItems: "center"}}>
                  <InformationIcon scale={0.8} />
                </div>
              </Tooltip>
            </div>
            <FormRowBlock data-testid="attributes-selectors">
              <ETAAttributeSelect
                control={control}
                name="attributes.serviceTypes"
                options={services}
                onQueryChange={setServiceTypeQuery}
                onLoadMore={() => {
                  servicesResults.refetch({
                    limit: services.length + 20,
                    ...(serviceTypeFilter && {filter: serviceTypeFilter})
                  });
                }}
                placeholder="Search services"
                buttonLabel="Service Type"
                labelText="Service Type(s)"
                testIdPrefix="service"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.stopTypes"
                options={filteredStopTypes}
                onQueryChange={setStopTypeQuery}
                placeholder="Select stop types"
                buttonLabel="Stop Type"
                labelText="Stop Type(s)"
                testIdPrefix="stopType"
                optionTooltips={{B: BOTH_STOP_TOOLTIP}}
                optionLabels={STOP_TYPE_DISPLAY_LABELS}
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.customers"
                options={customers}
                onQueryChange={setCustomerQuery}
                onLoadMore={() => {
                  customersResults.refetch({
                    limit: customers.length + 20,
                    ...(customerFilter && {filter: customerFilter})
                  });
                }}
                placeholder="Search customers"
                buttonLabel="Customer"
                labelText="Customer(s)"
                testIdPrefix="customer"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.customerGroups"
                options={filteredCustomerGroups}
                onQueryChange={setCustomerGroupQuery}
                placeholder="Search customer groups"
                buttonLabel="Customer Group"
                labelText="Customer Group(s)"
                testIdPrefix="customerGroup"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.vehicleTypes"
                options={vehicleTypes}
                onQueryChange={setVehicleTypeQuery}
                onLoadMore={() => {
                  vehicleTypesResults.refetch({
                    limit: vehicleTypes.length + 20,
                    ...(vehicleTypeFilter && {filter: vehicleTypeFilter})
                  });
                }}
                placeholder="Search vehicle types"
                buttonLabel="Vehicle Type"
                labelText="Vehicle Type(s)"
                testIdPrefix="vehicleType"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.priorities"
                options={priorities}
                onQueryChange={setPriorityQuery}
                onLoadMore={() => {
                  prioritiesResults.refetch({
                    limit: priorities.length + 20,
                    ...(priorityFilter && {filter: priorityFilter})
                  });
                }}
                placeholder="Search priorities"
                buttonLabel="Priority"
                labelText="Priorit(ies)"
                testIdPrefix="priority"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.sites"
                options={sites}
                onQueryChange={setSiteQuery}
                onLoadMore={() => {
                  sitesResults.refetch({
                    limit: sites.length + 20,
                    ...(siteFilter && {
                      filter: {
                        ...siteFilter,
                        status: {eq: "A"}
                      }
                    })
                  });
                }}
                placeholder="Search sites"
                buttonLabel="Site"
                labelText="Site(s)"
                testIdPrefix="site"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.drivers"
                options={drivers}
                onQueryChange={setDriverQuery}
                onLoadMore={() => {
                  driversResults.refetch({
                    limit: drivers.length + 20,
                    ...(driverFilter && {filter: driverFilter})
                  });
                }}
                placeholder="Search drivers"
                buttonLabel="Driver"
                labelText="Driver(s)"
                testIdPrefix="driver"
              />
              <ETAAttributeSelect
                control={control}
                name="attributes.dispatchGroups"
                options={driverGroups}
                onQueryChange={setDriverGroupQuery}
                onLoadMore={() => {
                  driverGroupsResults.refetch({
                    limit: driverGroups.length + 20,
                    ...(driverGroupFilter && {filter: driverGroupFilter})
                  });
                }}
                placeholder="Search driver groups"
                buttonLabel="Driver Group"
                labelText="Driver Group(s)"
                testIdPrefix="driverGroup"
              />
            </FormRowBlock>
            <ETASelectionRenderer title={"Service Type"} arrayOfValues={attributesValues.serviceTypes} />
            <ETASelectionRenderer
              title={"Stop Type"}
              arrayOfValues={formatStopTypesForDisplay(attributesValues.stopTypes)}
            />
            <ETASelectionRenderer title={"Customer"} arrayOfValues={attributesValues.customers} />
            <ETASelectionRenderer title={"Customer Group"} arrayOfValues={attributesValues.customerGroups} />
            <ETASelectionRenderer title={"Vehicle"} arrayOfValues={attributesValues.vehicleTypes} />
            <ETASelectionRenderer title={"Priority"} arrayOfValues={attributesValues.priorities} />
            <ETASelectionRenderer title={"Site"} arrayOfValues={attributesValues.sites} />
            <ETASelectionRenderer title={"Driver"} arrayOfValues={attributesValues.drivers} />
            <ETASelectionRenderer title={"Driver Group"} arrayOfValues={attributesValues.dispatchGroups} />
          </FormColumnBlock>
          <FormColumnBlock withDivider={true}>
            <FormColumnBlock gap={"4px"} padding={"0px"}>
              <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                <HeaderText>Notification sent via</HeaderText>
                <Tooltip
                  content={
                    <div>
                      <p>
                        Who are we going to send this to, and via what medium. If you put in a &apos;custom&apos; name,
                        keep in mind that every email or text that follows this will be sent to that number or email.
                      </p>
                    </div>
                  }
                  placement="top"
                  popoverClassName="content-tooltip"
                >
                  <div
                    data-testid="notification-sent-via-information-icon"
                    style={{display: "flex", alignItems: "center"}}
                  >
                    <InformationIcon scale={0.8} />
                  </div>
                </Tooltip>
              </div>
              <SubHeaderText>
                The {sendNotificationVia?.type === SendNotificationType.EMAIL ? "email" : "phone number"} on the stop in
                CMS will be who&#39;s {sendNotificationVia?.type === SendNotificationType.EMAIL ? "emailed" : "texted"}{" "}
                with the ETA time window
              </SubHeaderText>
            </FormColumnBlock>
            <ETANotificationSettings />
          </FormColumnBlock>
          {includeOptimizationETAEnabled && (
            <FormColumnBlock withDivider={true}>
              <Controller
                render={renderOptimizationNotificationSwitch}
                name={"optimizationNotification.enabled"}
                control={control}
              />
              <ETATriggerSettings
                disabled={!optimizationNotificationEnabled}
                fieldSuffix={"optimizationNotification"}
                triggerWhenText="Trigger when the optimization is complete"
              />
            </FormColumnBlock>
          )}
          <FormColumnBlock withDivider={true}>
            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
              <HeaderText>1st Notification</HeaderText>
              <Tooltip
                content={
                  <div>
                    <p>
                      Our goal is to let you say <strong>when the notification will be sent</strong> (X minutes or stops
                      away from current time/stop) and <strong>what time range is sent in the notification</strong> (X
                      minutes before to Y minutes after the ETA).
                    </p>
                  </div>
                }
                placement="top"
                popoverClassName="content-tooltip"
              >
                <div data-testid="first-notification-information-icon" style={{display: "flex", alignItems: "center"}}>
                  <InformationIcon scale={0.8} />
                </div>
              </Tooltip>
            </div>
            <ETATriggerSettings fieldSuffix={"firstNotification"} />
            <ETANotificationTimeline fieldSuffix="firstNotification" />
          </FormColumnBlock>
          <FormColumnBlock withDivider={true} className={!canEditPremium ? "premium-disabled-block" : ""}>
            <Controller
              render={renderSecondNotificationSwitch}
              name={"secondNotification.enabled"}
              control={control}
              disabled={!canEditPremium}
            />
            <ETATriggerSettings
              fieldSuffix={"secondNotification"}
              disabled={!canEditPremium || !isUseSecondNotification}
              crossFieldValidate={validateSecondNotificationAwayFrom}
            />
            <ETANotificationTimeline
              fieldSuffix="secondNotification"
              disabled={!canEditPremium || !isUseSecondNotification}
            />
          </FormColumnBlock>
          <FormColumnBlock>
            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
              <HeaderText>Follow Up</HeaderText>
              <Tooltip
                content={
                  <div>
                    <p>
                      When the ETA value falls outside of the prior range communicated, by having this clicked, it will
                      automatically send an updated time range based on the new ETA value using the time window range of
                      the previous communication.
                    </p>
                  </div>
                }
                placement="top"
                popoverClassName="content-tooltip"
              >
                <div data-testid="follow-up-information-icon" style={{display: "flex", alignItems: "center"}}>
                  <InformationIcon scale={0.8} />
                </div>
              </Tooltip>
            </div>
            <Controller render={renderIsFollowedUpCheckbox} name={"isFollowedUp"} control={control} />
          </FormColumnBlock>
          <ETAFooterContainer>
            {formMode === "edit" && (
              <SettingEditorButton intent="danger" onClick={handleDeleteClick} data-testid="color-logic-delete">
                Delete
              </SettingEditorButton>
            )}
            <NoBackGroundSettingEditorButton onClick={onCancel} data-testid="cancel-button">
              Cancel
            </NoBackGroundSettingEditorButton>
            <SettingEditorButton
              onClick={() => void handleSaveClick()}
              disabled={!isFormValid || (formMode === "edit" && !isDirty)}
              data-testid="save-button"
            >
              {formMode === "edit" ? "Save Changes" : "Create"}
            </SettingEditorButton>
          </ETAFooterContainer>
        </>
      )}
      <ConfirmationDialog
        testIdPrefix="follow-up-revert"
        isVisible={showRevertDialog}
        header="Window Doesn’t Meet Follow up Requirements"
        body="In order to have the follow up box checked, time windows must be at least 15 minutes before and 30 minutes after the ETA. Do you want to proceed with the new windows and remove the follow up?"
        yesText="Yes"
        noText="Cancel"
        handleYesClicked={handleConfirmInvalidChange}
        handleNoClicked={handleCancelInvalidChange}
        handleClose={handleCancelInvalidChange}
      />
      <ConfirmationDialog
        testIdPrefix="follow-up-partial-info"
        isVisible={!!followUpInfoMessage}
        header="Follow Up notifications"
        body={followUpInfoMessage ?? ""}
        yesText="Okay"
        yesTextColor="#137cbd"
        hideNoButton
        handleYesClicked={() => setFollowUpInfoMessage(null)}
        handleClose={() => setFollowUpInfoMessage(null)}
      />
    </ETANotificationFormContainer>
  );
};

const ETACreateNewIcon = () => {
  return (
    <svg width="104" height="101" viewBox="0 0 104 101" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M66.7719 10.2218C62.4325 7.96417 57.372 7.57499 52.7613 9.12456L22.3462 17.2743C17.5458 18.2464 13.358 21.1136 10.7287 25.2386C8.47099 29.578 8.0818 34.6384 9.64008 39.2817L17.7897 69.6966C18.7618 74.4969 21.6291 78.6847 25.754 81.314C30.0935 83.5716 35.1539 83.9608 39.7972 82.4025L70.2124 74.2528C74.9802 73.2894 79.2006 70.4134 81.8299 66.2885C84.055 61.9577 84.4767 56.8886 82.9185 52.2454L74.7688 21.8305C73.7641 17.0389 70.8969 12.8511 66.7719 10.2218Z"
        fill="url(#paint0_linear_3360_347)"
      />
      <g opacity="0.5" filter="url(#filter0_f_3360_347)">
        <path
          d="M51.4166 18.7934L28.4797 25.1913C20.1599 27.512 16.5106 33.1467 18.5579 40.4865L25.492 65.3453C27.5729 72.8053 33.5849 75.6414 41.9047 73.3207L64.8417 66.9227C73.2956 64.5646 76.8349 59.0644 74.754 51.6043L67.8199 26.7455C65.7726 19.4058 59.8705 16.4353 51.4166 18.7934Z"
          fill="#3530AD"
        />
      </g>
      <foreignObject x="4.20251" y="0.991776" width="123.365" height="123.365">
        <div
          style={{
            backdropFilter: "blur(12px)",
            clipPath: "url(#bgblur_0_3360_347_clip_path)",
            height: "100%",
            width: "100%"
          }}
        ></div>
      </foreignObject>
      <g data-figma-bg-blur-radius="24">
        <mask
          id="path-3-outside-1_3360_347"
          maskUnits="userSpaceOnUse"
          x="28.2025"
          y="24.9918"
          width="76"
          height="76"
          fill="black"
        >
          <rect fill="white" x="28.2025" y="24.9918" width="76" height="76" />
          <path d="M97.3995 31.1233C93.4786 27.5321 88.275 25.6999 82.9982 26.0297H48.772C43.4586 25.6999 38.255 27.5321 34.334 31.1233C30.7429 35.0442 28.9106 40.2477 29.2404 45.5611V79.787C28.9106 85.1004 30.7429 90.304 34.334 94.2249C38.255 97.8161 43.4586 99.6483 48.772 99.3185H82.9982C88.275 99.6483 93.5152 97.8161 97.4362 94.2249C100.991 90.304 102.86 85.1004 102.53 79.787V45.5611C102.823 40.2477 100.991 35.0442 97.3995 31.1233Z" />
        </mask>
        <path
          d="M97.3995 31.1233C93.4786 27.5321 88.275 25.6999 82.9982 26.0297H48.772C43.4586 25.6999 38.255 27.5321 34.334 31.1233C30.7429 35.0442 28.9106 40.2477 29.2404 45.5611V79.787C28.9106 85.1004 30.7429 90.304 34.334 94.2249C38.255 97.8161 43.4586 99.6483 48.772 99.3185H82.9982C88.275 99.6483 93.5152 97.8161 97.4362 94.2249C100.991 90.304 102.86 85.1004 102.53 79.787V45.5611C102.823 40.2477 100.991 35.0442 97.3995 31.1233Z"
          fill="#60ACDC"
          fillOpacity="0.45"
        />
        <path
          d="M96.7241 31.8607C97.1314 32.2337 97.764 32.2059 98.137 31.7987C98.51 31.3914 98.4822 30.7588 98.075 30.3858L97.3995 31.1233L96.7241 31.8607ZM82.9982 26.0297V27.0297C83.019 27.0297 83.0398 27.029 83.0606 27.0277L82.9982 26.0297ZM48.772 26.0297L48.7101 27.0278C48.7307 27.029 48.7514 27.0297 48.772 27.0297V26.0297ZM34.334 31.1233L33.6586 30.3858C33.6371 30.4056 33.6164 30.4263 33.5966 30.4478L34.334 31.1233ZM29.2404 45.5611H30.2404C30.2404 45.5405 30.2398 45.5198 30.2385 45.4992L29.2404 45.5611ZM29.2404 79.787L30.2385 79.849C30.2398 79.8283 30.2404 79.8077 30.2404 79.787H29.2404ZM34.334 94.2249L33.5966 94.9003C33.6164 94.9219 33.6371 94.9426 33.6586 94.9623L34.334 94.2249ZM48.772 99.3185V98.3185C48.7514 98.3185 48.7307 98.3191 48.7101 98.3204L48.772 99.3185ZM82.9982 99.3185L83.0606 98.3204C83.0398 98.3191 83.019 98.3185 82.9982 98.3185V99.3185ZM97.4362 94.2249L98.1116 94.9623C98.1344 94.9414 98.1563 94.9195 98.1771 94.8965L97.4362 94.2249ZM102.53 79.787H101.53C101.53 79.8077 101.53 79.8283 101.532 79.849L102.53 79.787ZM102.53 45.5611L101.531 45.5061C101.53 45.5244 101.53 45.5428 101.53 45.5611H102.53ZM98.137 30.4478C97.764 30.0406 97.1314 30.0128 96.7241 30.3858C96.3169 30.7588 96.2891 31.3914 96.6621 31.7987L97.3995 31.1233L98.137 30.4478ZM97.3995 31.1233L98.075 30.3858C93.9537 26.6112 88.4838 24.6849 82.9358 25.0316L82.9982 26.0297L83.0606 27.0277C88.0662 26.7149 93.0034 28.453 96.7241 31.8607L97.3995 31.1233ZM82.9982 26.0297V25.0297H48.772V26.0297V27.0297H82.9982V26.0297ZM48.772 26.0297L48.834 25.0316C43.2507 24.6851 37.7806 26.6105 33.6586 30.3858L34.334 31.1233L35.0094 31.8607C38.7294 28.4537 43.6665 26.7147 48.7101 27.0278L48.772 26.0297ZM34.334 31.1233L33.5966 30.4478C29.8213 34.5698 27.8958 40.0398 28.2423 45.6231L29.2404 45.5611L30.2385 45.4992C29.9255 40.4556 31.6644 35.5186 35.0715 31.7987L34.334 31.1233ZM29.2404 45.5611H28.2404V79.787H29.2404H30.2404V45.5611H29.2404ZM29.2404 79.787L28.2423 79.7251C27.8958 85.3084 29.8213 90.7783 33.5966 94.9003L34.334 94.2249L35.0715 93.5495C31.6644 89.8296 29.9255 84.8925 30.2385 79.849L29.2404 79.787ZM34.334 94.2249L33.6586 94.9623C37.7806 98.7376 43.2507 100.663 48.834 100.317L48.772 99.3185L48.7101 98.3204C43.6665 98.6334 38.7294 96.8945 35.0094 93.4875L34.334 94.2249ZM48.772 99.3185V100.318H82.9982V99.3185V98.3185H48.772V99.3185ZM82.9982 99.3185L82.9358 100.317C88.4821 100.663 93.9891 98.7381 98.1116 94.9623L97.4362 94.2249L96.7608 93.4875C93.0413 96.894 88.0679 98.6334 83.0606 98.3204L82.9982 99.3185ZM97.4362 94.2249L98.1771 94.8965C101.909 90.7795 103.875 85.3127 103.528 79.7251L102.53 79.787L101.532 79.849C101.845 84.8882 100.072 89.8284 96.6953 93.5533L97.4362 94.2249ZM102.53 79.787H103.53V45.5611H102.53H101.53V79.787H102.53ZM102.53 45.5611L103.528 45.6162C103.836 40.0378 101.912 34.57 98.137 30.4478L97.3995 31.1233L96.6621 31.7987C100.069 35.5185 101.81 40.4576 101.531 45.5061L102.53 45.5611Z"
          fill="url(#paint1_linear_3360_347)"
          mask="url(#path-3-outside-1_3360_347)"
        />
      </g>
      <foreignObject x="32.0844" y="26.0738" width="71.3994" height="71.3165">
        <div
          style={{
            backdropFilter: "blur(7.5px)",
            clipPath: "url(#bgblur_1_3360_347_clip_path)",
            height: "100%",
            width: "100%"
          }}
        ></div>
      </foreignObject>
      <g filter="url(#filter2_d_3360_347)" data-figma-bg-blur-radius="15">
        <mask id="path-5-inside-2_3360_347" fill="white">
          <path d="M86.8072 55.7132L62.1453 80.4482C60.8628 81.6941 59.1771 82.3904 57.4182 82.3904H48.8067C48.3303 82.3904 47.8905 82.2071 47.5607 81.8773C47.2309 81.5475 47.0844 81.1078 47.0844 80.6314L47.3042 71.9467C47.3409 70.2244 48.0371 68.6121 49.2464 67.4028L66.7259 49.9234C67.0191 49.6303 67.5321 49.6303 67.8252 49.9234L73.9449 56.0064C74.348 56.4095 74.9343 56.666 75.5573 56.666C76.9131 56.666 77.9758 55.5667 77.9758 54.2475C77.9758 53.5879 77.7193 53.0016 77.3162 52.5618C77.2063 52.4152 71.3798 46.6254 71.3798 46.6254C71.0133 46.259 71.0133 45.636 71.3798 45.2696L73.835 42.7778C76.1069 40.5058 79.7714 40.5058 82.0434 42.7778L86.8072 47.5415C89.0425 49.7768 89.0425 53.4413 86.8072 55.7132Z" />
        </mask>
        <path
          d="M86.8072 55.7132L62.1453 80.4482C60.8628 81.6941 59.1771 82.3904 57.4182 82.3904H48.8067C48.3303 82.3904 47.8905 82.2071 47.5607 81.8773C47.2309 81.5475 47.0844 81.1078 47.0844 80.6314L47.3042 71.9467C47.3409 70.2244 48.0371 68.6121 49.2464 67.4028L66.7259 49.9234C67.0191 49.6303 67.5321 49.6303 67.8252 49.9234L73.9449 56.0064C74.348 56.4095 74.9343 56.666 75.5573 56.666C76.9131 56.666 77.9758 55.5667 77.9758 54.2475C77.9758 53.5879 77.7193 53.0016 77.3162 52.5618C77.2063 52.4152 71.3798 46.6254 71.3798 46.6254C71.0133 46.259 71.0133 45.636 71.3798 45.2696L73.835 42.7778C76.1069 40.5058 79.7714 40.5058 82.0434 42.7778L86.8072 47.5415C89.0425 49.7768 89.0425 53.4413 86.8072 55.7132Z"
          fill="url(#paint2_linear_3360_347)"
        />
        <path
          d="M62.1453 80.4482L62.2847 80.5917L62.2869 80.5894L62.1453 80.4482ZM47.0844 80.6314L46.8844 80.6264V80.6314H47.0844ZM47.3042 71.9467L47.5042 71.9518L47.5042 71.951L47.3042 71.9467ZM49.2464 67.4028L49.3878 67.5442L49.2464 67.4028ZM66.7259 49.9234L66.5845 49.782L66.7259 49.9234ZM67.8252 49.9234L67.6838 50.0648L67.6842 50.0653L67.8252 49.9234ZM73.9449 56.0064L74.0863 55.865L74.0859 55.8645L73.9449 56.0064ZM77.3162 52.5618L77.1562 52.6818L77.1621 52.6897L77.1688 52.697L77.3162 52.5618ZM71.3798 46.6254L71.2384 46.7669L71.2388 46.7673L71.3798 46.6254ZM71.3798 45.2696L71.5212 45.411L71.5222 45.41L71.3798 45.2696ZM73.835 42.7778L73.6935 42.6363L73.6925 42.6374L73.835 42.7778ZM82.0434 42.7778L81.902 42.9192L82.0434 42.7778ZM86.8072 47.5415L86.9486 47.4001L86.8072 47.5415ZM86.8072 55.7132L86.6656 55.572L62.0037 80.307L62.1453 80.4482L62.2869 80.5894L86.9488 55.8545L86.8072 55.7132ZM62.1453 80.4482L62.006 80.3047C60.7597 81.5154 59.1237 82.1903 57.4182 82.1903V82.3904V82.5904C59.2305 82.5904 60.9658 81.8728 62.2847 80.5917L62.1453 80.4482ZM57.4182 82.3904V82.1903H48.8067V82.3904V82.5904H57.4182V82.3904ZM48.8067 82.3904V82.1903C48.383 82.1903 47.9942 82.028 47.7022 81.7359L47.5607 81.8773L47.4193 82.0187C47.7868 82.3863 48.2775 82.5904 48.8067 82.5904V82.3904ZM47.5607 81.8773L47.7022 81.7359C47.4174 81.4511 47.2844 81.0664 47.2844 80.6314H47.0844H46.8844C46.8844 81.1492 47.0445 81.6439 47.4193 82.0187L47.5607 81.8773ZM47.0844 80.6314L47.2843 80.6365L47.5042 71.9518L47.3042 71.9467L47.1043 71.9416L46.8844 80.6264L47.0844 80.6314ZM47.3042 71.9467L47.5042 71.951C47.5397 70.2807 48.2147 68.7173 49.3878 67.5442L49.2464 67.4028L49.105 67.2614C47.8595 68.5068 47.142 70.1682 47.1043 71.9424L47.3042 71.9467ZM49.2464 67.4028L49.3878 67.5442L66.8673 50.0648L66.7259 49.9234L66.5845 49.782L49.105 67.2614L49.2464 67.4028ZM66.7259 49.9234L66.8673 50.0648C67.0824 49.8498 67.4688 49.8498 67.6838 50.0648L67.8252 49.9234L67.9667 49.782C67.5954 49.4107 66.9557 49.4107 66.5845 49.782L66.7259 49.9234ZM67.8252 49.9234L67.6842 50.0653L73.8039 56.1482L73.9449 56.0064L74.0859 55.8645L67.9662 49.7816L67.8252 49.9234ZM73.9449 56.0064L73.8035 56.1478C74.2437 56.588 74.8807 56.866 75.5573 56.866V56.666V56.466C74.988 56.466 74.4523 56.231 74.0863 55.865L73.9449 56.0064ZM75.5573 56.666V56.866C77.0255 56.866 78.1758 55.6751 78.1758 54.2475H77.9758H77.7758C77.7758 55.4582 76.8007 56.466 75.5573 56.466V56.666ZM77.9758 54.2475H78.1758C78.1758 53.5344 77.8981 52.9007 77.4636 52.4267L77.3162 52.5618L77.1688 52.697C77.5405 53.1025 77.7758 53.6414 77.7758 54.2475H77.9758ZM77.3162 52.5618L77.4762 52.4418C77.4597 52.4199 77.4182 52.3765 77.3753 52.3321C77.3259 52.2809 77.2571 52.2106 77.172 52.1243C77.0018 51.9515 76.7648 51.713 76.4839 51.4312C75.922 50.8676 75.1832 50.1299 74.448 49.397C73.7127 48.6641 72.9809 47.9357 72.4329 47.3906C72.1589 47.118 71.9309 46.8913 71.7714 46.7327C71.6916 46.6534 71.6289 46.5911 71.5862 46.5486C71.5649 46.5274 71.5485 46.5111 71.5375 46.5002C71.532 46.4947 71.5278 46.4905 71.525 46.4878C71.5236 46.4864 71.5225 46.4853 71.5218 46.4846C71.5215 46.4843 71.5212 46.484 71.521 46.4838C71.5209 46.4837 71.5209 46.4837 71.5208 46.4836C71.5208 46.4836 71.5208 46.4836 71.3798 46.6254C71.2388 46.7673 71.2388 46.7673 71.2389 46.7674C71.2389 46.7674 71.239 46.7675 71.2391 46.7676C71.2392 46.7677 71.2395 46.768 71.2399 46.7684C71.2406 46.7691 71.2416 46.7701 71.243 46.7715C71.2458 46.7743 71.25 46.7784 71.2555 46.7839C71.2665 46.7949 71.2829 46.8111 71.3042 46.8323C71.3469 46.8748 71.4096 46.937 71.4893 47.0163C71.6489 47.1749 71.8769 47.4016 72.1508 47.6742C72.6988 48.2192 73.4305 48.9475 74.1656 49.6803C74.9007 50.4132 75.6392 51.1504 76.2006 51.7136C76.4814 51.9953 76.7177 52.2331 76.8871 52.405C76.9719 52.4911 77.0395 52.5602 77.0875 52.6099C77.1421 52.6664 77.159 52.6855 77.1562 52.6818L77.3162 52.5618ZM71.3798 46.6254L71.5212 46.484C71.2329 46.1957 71.2329 45.6993 71.5212 45.411L71.3798 45.2696L71.2384 45.1282C70.7938 45.5727 70.7938 46.3223 71.2384 46.7669L71.3798 46.6254ZM71.3798 45.2696L71.5222 45.41L73.9774 42.9181L73.835 42.7778L73.6925 42.6374L71.2373 45.1292L71.3798 45.2696ZM73.835 42.7778L73.9764 42.9192C76.1703 40.7253 79.7081 40.7253 81.902 42.9192L82.0434 42.7778L82.1848 42.6363C79.8347 40.2863 76.0436 40.2863 73.6935 42.6363L73.835 42.7778ZM82.0434 42.7778L81.902 42.9192L86.6658 47.683L86.8072 47.5415L86.9486 47.4001L82.1848 42.6363L82.0434 42.7778ZM86.8072 47.5415L86.6658 47.683C88.8224 49.8395 88.824 53.3782 86.6646 55.573L86.8072 55.7132L86.9498 55.8535C89.261 53.5044 89.2627 49.7142 86.9486 47.4001L86.8072 47.5415Z"
          fill="url(#paint3_linear_3360_347)"
          fillOpacity="0.5"
          mask="url(#path-5-inside-2_3360_347)"
        />
      </g>
      <defs>
        <filter
          id="filter0_f_3360_347"
          x="0"
          y="0"
          width="93.3364"
          height="92.1102"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="9" result="effect1_foregroundBlur_3360_347" />
        </filter>
        <clipPath id="bgblur_0_3360_347_clip_path" transform="translate(-4.20251 -0.991776)">
          <path d="M97.3995 31.1233C93.4786 27.5321 88.275 25.6999 82.9982 26.0297H48.772C43.4586 25.6999 38.255 27.5321 34.334 31.1233C30.7429 35.0442 28.9106 40.2477 29.2404 45.5611V79.787C28.9106 85.1004 30.7429 90.304 34.334 94.2249C38.255 97.8161 43.4586 99.6483 48.772 99.3185H82.9982C88.275 99.6483 93.5152 97.8161 97.4362 94.2249C100.991 90.304 102.86 85.1004 102.53 79.787V45.5611C102.823 40.2477 100.991 35.0442 97.3995 31.1233Z" />
        </clipPath>
        <filter
          id="filter2_d_3360_347"
          x="32.0844"
          y="26.0738"
          width="71.3994"
          height="71.3165"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="5" dy="5" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.166738 0 0 0 0 0.337003 0 0 0 0 0.847798 0 0 0 0.2 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3360_347" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3360_347" result="shape" />
        </filter>
        <clipPath id="bgblur_1_3360_347_clip_path" transform="translate(-32.0844 -26.0738)">
          <path d="M86.8072 55.7132L62.1453 80.4482C60.8628 81.6941 59.1771 82.3904 57.4182 82.3904H48.8067C48.3303 82.3904 47.8905 82.2071 47.5607 81.8773C47.2309 81.5475 47.0844 81.1078 47.0844 80.6314L47.3042 71.9467C47.3409 70.2244 48.0371 68.6121 49.2464 67.4028L66.7259 49.9234C67.0191 49.6303 67.5321 49.6303 67.8252 49.9234L73.9449 56.0064C74.348 56.4095 74.9343 56.666 75.5573 56.666C76.9131 56.666 77.9758 55.5667 77.9758 54.2475C77.9758 53.5879 77.7193 53.0016 77.3162 52.5618C77.2063 52.4152 71.3798 46.6254 71.3798 46.6254C71.0133 46.259 71.0133 45.636 71.3798 45.2696L73.835 42.7778C76.1069 40.5058 79.7714 40.5058 82.0434 42.7778L86.8072 47.5415C89.0425 49.7768 89.0425 53.4413 86.8072 55.7132Z" />
        </clipPath>
        <linearGradient
          id="paint0_linear_3360_347"
          x1="70.1423"
          y1="4.43124"
          x2="38.5791"
          y2="70.5115"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6FCFFB" />
          <stop offset="1" stopColor="#2659AB" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_3360_347"
          x1="40.881"
          y1="34.5388"
          x2="87.6798"
          y2="92.5003"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.25" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_3360_347"
          x1="85.5485"
          y1="48.5347"
          x2="39.4338"
          y2="50.0413"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0.5" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_3360_347"
          x1="51.544"
          y1="54.9335"
          x2="86.6977"
          y2="55.128"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};
